import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { BioContent } from '@onesource/components';
import { getMediaUrl } from '@onesource/services';
import { FC } from 'react';

import { DataShape } from './revisions-utils';

type Props = {
  dataKey: keyof DataShape;
  value: unknown;
  professionMap: Map<string, string>;
  expertiseMap: Map<string, string>;
  geoMap: Map<string, string>;
};

export const RevisionFieldValue: FC<Props> = ({ dataKey, value, professionMap, expertiseMap, geoMap }) => {
  if (value === null || value === undefined) {
    return (
      <Typography variant="body2" color="text.disabled">
        —
      </Typography>
    );
  }

  if (dataKey === 'bio' && typeof value === 'string') {
    return (
      <Typography variant="body2" component="div">
        <BioContent html={value} />
      </Typography>
    );
  }

  if (dataKey === 'casual_image' && typeof value === 'string' && value) {
    return (
      <Box
        component="img"
        src={getMediaUrl(value) || value}
        alt="profile"
        sx={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 1 }}
      />
    );
  }

  if (dataKey === 'socials' && typeof value === 'object' && value !== null) {
    const entries = Object.entries(value as Record<string, unknown>).filter(([, v]) => v && String(v).trim());
    if (entries.length === 0) {
      return (
        <Typography variant="body2" color="text.disabled">
          —
        </Typography>
      );
    }
    return (
      <Stack gap={0.5}>
        {entries.map(([platform, url]) => (
          <Typography key={platform} variant="body2">
            <strong>{platform}:</strong> {String(url)}
          </Typography>
        ))}
      </Stack>
    );
  }

  if (Array.isArray(value)) {
    if (value.length === 0) {
      return (
        <Typography variant="body2" color="text.disabled">
          —
        </Typography>
      );
    }
    return (
      <Stack direction="row" flexWrap="wrap" gap={0.5}>
        {value.map((v, i) => {
          // `expertise` holds a mix of lookup ids and un-backfilled free text — the
          // map lookup resolves the former and falls through for the latter.
          const lookup = dataKey === 'profession' ? professionMap : dataKey === 'expertise' ? expertiseMap : undefined;
          const label = lookup?.get(String(v)) ?? String(v);
          // oxlint-disable-next-line react/no-array-index-key
          return <Chip key={i} label={label} size="small" variant="outlined" />;
        })}
      </Stack>
    );
  }

  const displayValue =
    dataKey === 'business_country_id' || dataKey === 'business_state_id' || dataKey === 'business_city_id'
      ? (geoMap.get(String(value)) ?? String(value))
      : String(value);

  return (
    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
      {displayValue}
    </Typography>
  );
};
